import crypto from 'crypto';
import { createSHA256 } from 'hash-wasm';
// import tced_addon from '@tencent/tced-addon';

import md5 from '@/renderer/scripts/libs/md5';
import crc64 from '@/renderer/scripts/libs/crc64';
import { IS_BROWSER } from '@/common/constant/platform';
import { transformErrorForSerialization } from '@/common/tools';
import { TaskStatus } from '@/renderer/scripts/utils/TaskManager/types';

let fs: typeof import('fs');
let tced_addon: {
  crc: {
    crc64File: (paths: string, callback: Function) => void;
  };
};
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
  tced_addon = (window.parent as any).require('@tencent/tced-addon');
}
export interface FileSteam {
  // @ts-ignore
  stream: fs.ReadStream | Blob;
  type: 'ReadStream' | 'Blob';
}

/**
 * @description 获取文件分片信息
 * @export
 * @param {*} FilePath
 * @param {*} start
 * @param {*} end
 * @param {function} callback
 */
export function fileSlice(
  params: {
    filePath?: string;
    body?: Blob;
  },
  start: number,
  end: number,
  maxChunkSize?: number,
): FileSteam {
  if (params.filePath) {
    const readStream = fs.createReadStream(params.filePath, {
      start,
      end: end - 1,
      highWaterMark: maxChunkSize ? maxChunkSize : 64 * 1024,
    });
    return {
      stream: readStream,
      type: 'ReadStream',
    };
  }
  // 需要留意，TDesign升级之后，文件改了位置在raw属性中，所以需要从raw尝试取值(兼容两种方式)
  const file = (() => {
    const body = params?.body as File;
    if (typeof body?.slice === 'function') {
      return body;
    }
    // @ts-ignore
    if (typeof body?.raw?.slice === 'function') {
      // @ts-ignore
      return body?.raw;
    }
    return null;
  })();
  return {
    stream: file && file.slice(start, end),
    type: 'Blob',
  };
}
export function getFileMd5(data: FileSteam): Promise<string> {
  if (data.type === 'ReadStream') {
    return new Promise((resolve, reject) => {
      const md5 = crypto.createHash('md5');
      data.stream.on('data', (chunk: any) => {
        md5.update(chunk);
      });
      data.stream.on('error', (err: any) => {
        reject(err);
      });
      data.stream.on('end', () => {
        const hash = md5.digest('hex');
        data.stream.close();
        data.stream.destroy();
        resolve(hash);
      });
    });
  }
  return new Promise((resolve, reject) => {
    const blobSlice = File.prototype.slice;
    const chunkSize = 2 * 1024 * 1024;
    const chunks = Math.ceil(data.stream.size / chunkSize);
    let currentChunk = 0;
    // @ts-ignore
    let md5Ctx = md5.getCtx();
    const fileReader = new FileReader();
    function loadNext() {
      const start = currentChunk * chunkSize;
      const end = start + chunkSize >= data.stream.size ? data.stream.size : start + chunkSize;
      fileReader.readAsBinaryString(blobSlice.call(data.stream, start, end));
    }
    fileReader.onload = (e: any) => {
      md5Ctx = md5Ctx.update(e.target.result, true);
      currentChunk++;
      if (currentChunk < chunks) {
        loadNext();
      } else {
        resolve(md5Ctx.digest('hex'));
      }
    };
    fileReader.onerror = () => {
      reject(fileReader.error);
      fileReader.abort();
    };
    loadNext();
  });
}
export function binaryBase64(str: string): string {
  let i;
  let len;
  let char;
  const arr = [];
  for (i = 0, len = str.length / 2; i < len; i++) {
    char = parseInt(str[i * 2] + str[i * 2 + 1], 16);
    arr.push(char);
  }
  return Buffer.from(arr).toString('base64');
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
export function noop(): void {}

export function isRunning(status: TaskStatus): boolean {
  return status === TaskStatus.running || status === TaskStatus.waiting;
}

/**
 * TODO：这里暂时只能将上传的crc64计算换成c++版本。
 * 原因：c++版本的crc64还未支持只计算某一块的crc64值。
 */
export function getUploadCrc64Promise(path: string, readStreamOptions: CoFile.ReadStreamOptions): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const startTime = Date.now();
      tced_addon.crc.crc64File(path, (err: any, retStr: any) => {
        if (err) return reject(err);
        const costTime = (Date.now() - startTime) / 1000;
        logger.ui(`[crc64] [upload] [${path}] crc64 cost ${costTime}s, crc64Str: ${retStr}`);
        return resolve(retStr);
      });
    } catch (err) {
      return reject(err);
    }
  });
}

export function getCrc64Promise(path: string, readStreamOptions: CoFile.ReadStreamOptions): Promise<string> {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    crc64.crc64File(path, readStreamOptions, (err, crc64Str) => {
      if (err) return reject(err);
      const costTime = (Date.now() - startTime) / 1000;
      logger.ui(`[crc64] [${path}] crc64 cost ${costTime}s, crc64Str: ${crc64Str}`);
      return resolve(crc64Str);
    });
  });
}

// @ts-ignore
export function getDataFromStream(readStream: fs.ReadStream): Promise<Buffer> {
  const buffers: any[] = [];
  return new Promise((resolve, reject) => {
    readStream.on('data', (chunk: any) => {
      buffers.push(chunk);
    });
    readStream.on('error', (err: any) => {
      reject(err);
    });
    readStream.on('end', () => {
      readStream.close();
      readStream.destroy();
      resolve(Buffer.concat(buffers));
    });
  });
}
export function getDataFromBlob(readStream: Blob): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onloadend = (e: any) => {
      if (readStream.size === e.target.result.byteLength) {
        resolve(Buffer.from(e.target.result));
      } else {
        logger.ui.error('getDataFromBlob error', fileReader.error);
        reject(fileReader.error);
      }
    };
    fileReader.onerror = () => {
      fileReader.abort();
    };
    fileReader.readAsArrayBuffer(readStream);
  });
}
export async function getBufferFromStream(data: FileSteam) {
  let arrayBuffer: Buffer;
  try {
    if (data.type === 'ReadStream') {
      arrayBuffer = await getDataFromStream(data.stream);
    } else {
      arrayBuffer = await getDataFromBlob(data.stream);
    }
    return arrayBuffer;
  } catch (error) {
    logger.ui.error('getBufferFromStream error', error);
  }
  return undefined;
}
export function getSHA256FromStream(
  // @ts-ignore
  readStream: fs.ReadStream,
  blockSize: number,
  onProgress: Function,
  beginningHash: string,
): Promise<string> {
  let count = 0;
  let progress = 0;
  let sha256 = crypto.createHash('SHA256');
  let fullHash = beginningHash;
  sha256.update(Buffer.from(beginningHash, 'hex'));
  return new Promise((resolve, reject) => {
    readStream.on('data', (chunk: any) => {
      progress += chunk.length;
      if (!onProgress(progress)) {
        readStream.close();
        readStream.destroy();
        reject('read stop');
        return;
      }
      if (count + chunk.length < blockSize) {
        sha256.update(chunk);
        count += chunk.length;
      } else {
        const len = blockSize - count;
        sha256.update(chunk.slice(0, Math.min(len, chunk.length)));
        if (len <= chunk.length) {
          fullHash = sha256.digest('hex');
          sha256 = crypto.createHash('SHA256');
          sha256.update(Buffer.from(fullHash, 'hex'));
          count = chunk.length - len;
          if (count) {
            sha256.update(chunk.slice(len, chunk.length));
          }
        }
      }
    });
    readStream.on('error', (err: any) => {
      reject(err);
    });
    readStream.on('end', () => {
      readStream.close();
      readStream.destroy();
      if (count !== 0) {
        fullHash = sha256.digest('hex');
      }
      resolve(fullHash);
    });
  });
}
export async function getSHA256FromBlob(
  readStream: Blob,
  chunkSize: number,
  onProgress: Function,
  beginningHash: string,
): Promise<string | undefined> {
  let fullHash = beginningHash;
  let progress = 0;
  let sha256: any;
  let useWams = true;
  try {
    sha256 = await createSHA256();
    sha256.update(Buffer.from(fullHash, 'hex'));
  } catch (error: any) {
    logger.ui.error('getSHA256FromBlob error', error);
    // 使用JS版本
    useWams = false;
    sha256 = crypto.createHash('SHA256');
    sha256.update(Buffer.from(fullHash, 'hex'));
  }
  return new Promise((resolve, reject) => {
    const blobSlice = File.prototype.slice;
    const chunks = Math.ceil(readStream.size / chunkSize);
    let currentChunk = 0;
    const fileReader = new FileReader();
    function loadNext() {
      const start = currentChunk * chunkSize;
      const end = start + chunkSize >= readStream.size ? readStream.size : start + chunkSize;
      fileReader.readAsArrayBuffer(blobSlice.call(readStream, start, end));
    }
    fileReader.onload = async (e: any) => {
      progress += e.target.result.byteLength;
      if (!onProgress(progress)) {
        reject('read stop');
        fileReader.abort();
        return;
      }
      currentChunk++;
      sha256.update(Buffer.from(e.target.result));
      fullHash = sha256.digest('hex');
      if (currentChunk < chunks) {
        if (useWams) {
          sha256 = await createSHA256();
        } else {
          sha256 = crypto.createHash('SHA256');
        }
        sha256.update(Buffer.from(fullHash, 'hex'));
        loadNext();
      } else {
        resolve(fullHash);
      }
    };
    fileReader.onerror = () => {
      reject(fileReader.error);
      fileReader.abort();
    };
    loadNext();
  });
}
export async function getFileSHA256(data: FileSteam, blockSize: number, onProgress: Function, beginningHash?: string) {
  try {
    if (data.type === 'ReadStream') {
      return await getSHA256FromStream(data.stream, blockSize, onProgress, beginningHash || '');
    }
    return await getSHA256FromBlob(data.stream, blockSize, onProgress, beginningHash || '');
  } catch (error) {
    logger.ui.error('getFileSHA256 error', error);
  }
  return undefined;
}
export async function checkFileExistInBrowser(data: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const { stream } = fileSlice(
      {
        body: data,
      },
      0,
      1,
    );
    const fileReader = new FileReader();
    const start = 0;
    const end = 1;
    const blobSlice = File.prototype.slice;
    fileReader.readAsBinaryString(blobSlice.call(stream, start, end));
    fileReader.onload = (ev: ProgressEvent<FileReader>) => {
      resolve('');
    };
    fileReader.onerror = (ev: ProgressEvent<FileReader>) => {
      fileReader.abort();
      // 本地文件异常
      const domError = (ev.currentTarget as FileReader).error as DOMException;
      const err = transformErrorForSerialization(domError);
      reject({
        ...err,
        code: err.name,
      });
    };
  });
}

/**
 * @description 将后端返回的path进行一次encode
 * @export
 * @param {string} path
 * @return {*}
 */
export function pathEncode(path: string) {
  const pathSplit = path.split('?');
  const [pathName, query] = pathSplit;
  const encodePathName = pathName
    .split('/')
    .map(s => encodeURIComponent(s))
    .join('/');
  if (query) return [encodePathName, query].join('?');
  return encodePathName;
}
